// Base model
import ExtendModel from '@/models/ExtendModel'

// Actions models
import AuthorizeModel from '@/models/Actions/AAuthorize'
import CloseModel from '@/models/Actions/AClose'
import ClosePartiallyModel from '@/models/Actions/AClosePartially'
import ACloseRectifiedModel from '@/models/Actions/ACloseRectified'
import RejectModel from '@/models/Actions/AReject'
import ARectifyModel from '@/models/Actions/ARectify'
import SentModel from '@/models/Actions/ASent'
import ASentToAuthorizedModel from '@/models/Actions/ASentToAuthorized'

// Collections models
import DetailPurchaseOrderModel from '@/models/Purchase/PchDetailPurchaseOrder'
import ProductServiceIndicatorModel from '@/models/Warehouse/WhProductServiceIndicator'

/**
 * This class describes a pch purchase order.
 *
 * @class      PchPurchaseOrder (name)
 */
export default class PchPurchaseOrder extends ExtendModel {
    //
    pch_selected_purchase_orders = []

    //
    static includes = [
        'g_authorizer_user.hr_employee',
        'g_originator_user.hr_employee',
        'pch_detail_purchase_orders.wh_product',
        'pch_detail_purchase_orders.wh_product_service',
        'pch_payment_condition',
        'pch_provider',
        'pch_provider_branch_offices',
        'pch_purchase_order_has_invoice_numbers.pch_purchase_invoice',
        'pch_purchase_order_state',
        'wh_orderers.g_supervisor_user',
        'wh_orderers.pch_provider',
        'wh_orderers.pch_provider_branch_offices',
        //'wh_product_receptions',
        'wh_warehouse.g_branch_office.g_commune',
    ]

    /**
     * @description
     */
    get can_authorize() {
        return this.pch_purchase_order_state_id === 1
    }

    /**
     * @description
     */
    get can_close() {
        return (
            this.pch_purchase_order_state_id > 3 &&
            this.pch_purchase_order_state_id < 6
        )
    }

    /**
     * @description
     */
    get can_edit() {
        return this.pch_purchase_order_state_id < 2
    }

    /**
     * 
     */
    get can_rectify() {
        return this.pch_purchase_order_state_id === 6
    }

    /**
     * @description
     */
    get can_sent() {
        return this.pch_purchase_order_state_id === 3
    }

    /**
     * @description
     */
    get can_sent_to_authorized() {
        return this.pch_purchase_order_state_id === 4
    }

    /**
     * @description
     */
    get g_originator_user_full_name() {
        return [
            this.g_originator_user.hr_employee.names,
            this.g_originator_user.hr_employee.last_name1,
            this.g_originator_user.hr_employee.last_name2,
        ].join(' ')
    }

    /**
     * @description
     */
    get _invoice_numbers() {
        return this.pch_purchase_order_has_invoice_numbers.map(
            this.mapInvoiceNumber
        )
    }

    /**
     * @description
     */
    set _invoice_numbers(items) {
        this.pch_purchase_order_has_invoice_numbers = items.map(
            this.mapInvoiceNumbers
        )
    }

    /**
     * @description
     */
    get is_authorized() {
        return this.pch_purchase_order_state_id === 3
    }

    /**
     * @description
     */
    get pch_provider_alias_name() {
        return this.pch_provider.alias_name
    }

    /**
     * @description
     */
    get pch_provider_branch_offices_address() {
        return this.pch_provider_branch_offices.address
    }

    /**
     * @description
     */
    get pch_purchase_order_state_name() {
        return this.pch_purchase_order_state
            ? this.pch_purchase_order_state.state
            : '-'
    }

    /**
     *
     */
    get pch_selected_purchase_orders_net_price() {
        return Math.ceil(
            this.pch_selected_purchase_orders.reduce(this.reduceNetPrice, 0)
        )
    }

    /**
     *
     */
    get pch_selected_purchase_orders_net_price_real() {
        return Math.ceil(
            this.pch_selected_purchase_orders.reduce(this.reduceNetPriceReal, 0)
        )
    }

    /**
     *
     */
    get pch_selected_purchase_orders_net_price_str() {
        return '$ ' + this.pch_selected_purchase_orders_net_price
    }

    /**
     * @description
     */
    get wh_orderers_length() {
        return this.wh_orderers.length
    }

    /**
     * @description
     * wh product service indicator id
     *
     * @type       {number,null}
     */
    get wh_product_service_indicator_id() {
        return this.wh_product_service_indicator ? this.wh_product_service_indicator.wh_product_id : null
    }

    /**
     * @description
     */
    get wh_warehouse_name() {
        return this.wh_warehouse.name
    }

    /**
     * @description
     */
    authorize() {
        return this.hasMany(AuthorizeModel)
    }

    /**
     * @description
     */
    close() {
        return this.hasMany(CloseModel).attach(this.toClose())
    }

    /**
     * @description
     */
    closePartially() {
        return this.hasMany(ClosePartiallyModel).attach(this.toClose())
    }

    /**
     * @description
     */
    closeRectified() {
        return this.hasMany(ACloseRectifiedModel)
    }

    /**
     * @description
     */
    filterByReceived(item) {
        return item.received > 0
    }

    /**
     * @description
     * Fetches a product service indicator.
     */
    fetchProductServiceIndicator = () => {
        return ProductServiceIndicatorModel
            .get()
            .then(this.setProductServiceIndicator)
    }

    /**
     * @description
     * Mapping product detail to post/put request
     */
    mapDetail = item => ({
        net_price: Number(item.net_price),
        provider_product_code: String(item.provider_product_code || ''),
        quantity: Number(item.quantity),
        wh_product_id: this.is_service ? Number(this.wh_product_service_indicator_id) : Number(item.wh_product_id),
        wh_product_service_id: item.wh_product_service_id ? Number(item.wh_product_service_id) : null
    })

    /**
     * 
     */
    mapDetailRectify = item => ({
        wh_product_id: this.is_service ? Number(this.wh_product_service_indicator_id) : Number(item.wh_product_id),
        wh_product_service_id: item.wh_product_service_id ? Number(item.wh_product_service_id) : null,
        net_price_real: Number(item.net_price_real),
    })

    /**
     * @description
     */
    mapInvoiceNumber = ({ invoice_number }) => invoice_number

    /**
     * @description
     */
    mapInvoiceNumbers = item => ({
        invoice_number: item,
    })

    /**
     * @description
     * Mapping product detail to reception
     *
     * @param      {object}  item    The item
     */
    mapReception = item => ({
        received: Number(item.receive),
        wh_product_id: Number(item.wh_product_id),
        wh_product_service_id: Number(item.wh_product_service_id),
    })

    /**
     * @description
     * Mapping detail to editable selected collection
     *
     * @param      {<type>}  item    detail item
     * @return     {Object}  editable item
     *
    mapSelected = item => ({
        id: Number(item.id || 0),
        internal_code: String(item.wh_product.internal_code),
        name: String(this.is_service ? item.wh_product_service.name : item.wh_product.name),
        net_price: Number(item.net_price || 0),
        provider_product_code: String(item.provider_product_code || ''),
        quantity: Number(item.quantity),
        received: Number(item.received),
        status: String(item.status),
        wh_product: Object(item.wh_product),
        wh_product_id: Number(item.wh_product_id),
        wh_product_service: Object(item.wh_product_service),
        wh_product_service_id: Number(item.wh_product_service_id),
    })*/
    mapSelected = item => new DetailPurchaseOrderModel(item)

    /**
     * @description
     */
    rectify() {
        return this.hasMany(ARectifyModel)
    }

    /**
     *
     */
    reduceNetPrice(total, current) {
        return (
            Number(total) +
            Math.ceil(Number(current.net_price * current.quantity))
        )
    }

    /**
     *
     */
    reduceNetPriceReal(total, current) {
        return (
            Number(total) +
            Math.ceil(Number(current.net_price_real * current.quantity))
        )
    }

    /**
     * @description
     */
    reject() {
        return this.hasMany(RejectModel)
    }

    /**
     * @description
     */
    reset() {

        //
        this.fetchProductServiceIndicator()

        //
        this.pch_selected_purchase_orders = this.pch_detail_purchase_orders
            ? this.pch_detail_purchase_orders.map(this.mapSelected)
            : []

        //
        return this
    }

    /**
     * @description
     */
    resource() {
        return 'pch_purchase_order'
    }

    /**
     *
     */
    sent() {
        return this.hasMany(SentModel)
    }

    /**
     *
     */
    sentToAuthorized() {
        return this.hasMany(ASentToAuthorizedModel)
    }

    /**
     * @description
     * Sets the product service indicator.
     */
    setProductServiceIndicator = data => {
        this.wh_product_service_indicator = data.slice().shift()
    }

    /**
     * @description
     */
    toClose() {
        //
        this.details = this.pch_selected_purchase_orders
            .map(this.mapReception)
            .filter(this.filterByReceived)

        //
        this.invoice_numbers = this._invoice_numbers

        //
        return this.clean([
            'details',
            'invoice_numbers',
            'reception_comment',
            'wh_warehouse_reception_id',
        ])
    }

    /**
     * @description
     */
    toCreate() {
        //
        this.details = this.pch_selected_purchase_orders.map(this.mapDetail)
        this.orderers = this.orderers || []

        //
        return this.clean([
            'details',
            'g_originator_user_id',
            'is_exent',
            'is_service',
            'pch_payment_condition_id',
            'pch_provider_id',
            'pch_provider_branch_offices_id',
            'observation',
            'orderers',
            'wh_warehouse_id',
        ])
    }

    toRectify() {
        return {
            details: this.pch_selected_purchase_orders.map(this.mapDetailRectify)
        }
    }

    /**
     * Returns an update representation of the object.
     *
     * @return     {PchDetailPurchaseOrder}  Update representation of the object.
     */
    toUpdate() {
        //
        this.details = this.pch_selected_purchase_orders.map(this.mapDetail)
        this.orderers = this.orderers || []

        //
        return this.clean([
            'details',
            'g_originator_user_id',
            'id',
            'is_exent',
            'is_service',
            'pch_payment_condition_id',
            'pch_provider_id',
            'pch_provider_branch_offices_id',
            'observation',
            'orderers',
            'wh_warehouse_id',
        ])
    }

    get invoiceNumbers() {
        return this.pch_purchase_order_has_invoice_numbers
            .map(o => o.invoice_number)
            .join(', ')
    }
}
